import { Bell, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useHelp } from "@/lib/help.ts";
import { REMINDER_HOURS, hourLabel } from "@/lib/reminders.ts";
import { useReminder } from "@/lib/useReminder.ts";
import { cn } from "@/lib/utils";

/**
 * "Keep your streak": a daily nudge at the hour the player picks, skipped on days they've already played.
 * Renders nothing where reminders can't work (server not set up, or a browser with no push).
 * `onlyWhenOff` is for the result screen, where a card for something already switched on would just be noise.
 */
export function ReminderCard({ className, onlyWhenOff = false }: { className?: string; onlyWhenOff?: boolean }) {
  const r = useReminder();
  if (r.availability === "unavailable") return null;
  if (onlyWhenOff && (r.on || r.availability !== "ready")) return null;
  const locked = r.availability !== "ready";

  return (
    <section aria-labelledby="reminder-h" className={cn("w-full rounded-xl border border-border bg-card px-5 py-4 text-left font-ui shadow-sm", className)}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h3 id="reminder-h" className="flex items-center gap-2 font-display text-xl leading-tight">
            {r.on ? <BellRing className="size-4 text-brand" aria-hidden /> : <Bell className="size-4 text-muted-foreground" aria-hidden />}
            Keep your streak
          </h3>
          <p id="reminder-d" className="mt-1 text-sm leading-relaxed text-muted-foreground">
            A nudge each day when the verse is ready, and none on days you’ve already played.
          </p>
        </div>
        <Switch checked={r.on} disabled={r.busy || locked} onCheckedChange={(v) => void (v ? r.turnOn() : r.turnOff())} aria-label="Daily reminder" aria-describedby="reminder-d" />
      </div>

      {r.availability === "install-first" && (
        <p className="mt-3 text-sm leading-relaxed">
          On iPhone and iPad, reminders work from the home screen app.{" "}
          <button type="button" onClick={() => useHelp.getState().show("how")} className="font-medium text-brand underline underline-offset-4">
            Show me how to add it
          </button>
          , open it from there, and this switch will come alive.
        </p>
      )}
      {r.availability === "blocked" && <p className="mt-3 text-sm leading-relaxed">Notifications are turned off for this site. Allow them in your browser or phone settings, then come back and switch this on.</p>}

      {r.on && (
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
          <span id="reminder-hour-l">Remind me at</span>
          <Select value={String(r.hour)} onValueChange={(v) => void r.setHour(Number(v))}>
            <SelectTrigger size="sm" aria-labelledby="reminder-hour-l" className="w-[7.5rem] font-ui">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REMINDER_HOURS.map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {hourLabel(h)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => void r.sendTest()} disabled={r.testState !== "idle"} className="ml-auto text-muted-foreground">
            {r.testState === "sending" ? "Sending…" : r.testState === "sent" ? "Sent. Check your notifications" : "Send me a test"}
          </Button>
        </div>
      )}

      {r.error && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {r.error}
        </p>
      )}

      <p className="mt-3 text-xs leading-snug text-muted-foreground">
        To send it we keep a device address, your time zone, your reminder time and the date you last played. No account, nothing else, and switching this off deletes it.
      </p>
    </section>
  );
}
